import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { api, formatRupiah, formatErr } from "../lib/api";
import { Trash2, Tag, CreditCard } from "lucide-react";
import { toast } from "sonner";

const PAYMENTS = [
  { id: "stripe", label: "Stripe", desc: "Kartu kredit internasional" },
  { id: "xendit", label: "Xendit", desc: "Bank transfer, e-wallet (OVO, DANA, GoPay)" },
  { id: "midtrans", label: "Midtrans", desc: "QRIS, Virtual Account, Kartu Kredit" },
];

export default function Checkout() {
  const { cart, removeItem, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [pm, setPm] = useState("xendit");
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    customer_email: user?.email || "",
    customer_phone: user?.phone || "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);

  const applyVoucher = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/vouchers/validate", { code: code.trim(), subtotal });
      setVoucher(data.voucher);
      setDiscount(data.discount);
      toast.success(`Voucher diterapkan: -${formatRupiah(data.discount)}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Voucher tidak valid");
      setVoucher(null); setDiscount(0);
    }
  };

  const total = Math.max(0, subtotal - discount);

  const placeOrder = async () => {
    if (cart.length === 0) { toast.error("Keranjang kosong"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        items: cart.map(({ image, ...i }) => i),
        voucher_code: voucher?.code || null,
        payment_method: pm,
        ...form,
      });
      // Mock payment: auto-confirm
      await api.post(`/orders/${data.order.id}/pay-mock`);
      clear();
      toast.success("Pembayaran berhasil!");
      navigate(`/order/${data.order.id}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Order gagal");
    } finally { setLoading(false); }
  };

  if (cart.length === 0) {
    return (
      <div className="lumina-container py-32 text-center" data-testid="checkout-empty">
        <h2 className="font-serif text-3xl text-[#1A1728] mb-4">Keranjang kamu kosong</h2>
        <p className="text-[#5C5870] mb-6">Yuk, jelajahi produk & layanan kami.</p>
        <Link to="/products" className="lumina-btn-primary">Mulai belanja</Link>
      </div>
    );
  }

  return (
    <div className="lumina-container py-12" data-testid="checkout-page">
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#1A1728] mb-10">Checkout</h1>
      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 space-y-6">
          <div className="lumina-card p-7">
            <h2 className="font-serif text-2xl mb-5">Ringkasan Pesanan</h2>
            <div className="space-y-4">
              {cart.map((i, idx) => (
                <div key={`${i.item_id}-${idx}`} className="flex gap-4 items-center pb-4 border-b border-[#E2DFED] last:border-0 last:pb-0" data-testid={`cart-item-${idx}`}>
                  <img src={i.image} alt={i.title} className="w-20 h-20 rounded-2xl object-cover" />
                  <div className="flex-1">
                    <div className="text-xs text-[#5C5870] uppercase tracking-wider">{i.item_type}</div>
                    <div className="font-medium text-[#1A1728]">{i.title}</div>
                    {i.schedule && <div className="text-xs text-[#6B4BA3] mt-1">{new Date(i.schedule.split(" ")[0] || i.schedule).toLocaleDateString("id-ID")} {i.schedule.includes(" ") && i.schedule.split(" ")[1]}</div>}
                    <div className="text-sm text-[#6B4BA3] font-semibold mt-1">{formatRupiah(i.price * i.quantity)}</div>
                  </div>
                  <button onClick={() => removeItem(i.item_id, i.item_type)} className="text-[#E57373] hover:text-red-600 p-2" data-testid={`remove-${idx}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="lumina-card p-7">
            <h2 className="font-serif text-2xl mb-5">Data Pemesan</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <input placeholder="Nama" value={form.customer_name} onChange={e=>setForm({...form,customer_name:e.target.value})} className="px-4 py-3 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" data-testid="checkout-name" />
              <input type="email" placeholder="Email" value={form.customer_email} onChange={e=>setForm({...form,customer_email:e.target.value})} className="px-4 py-3 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" data-testid="checkout-email" />
              <input placeholder="No. WhatsApp" value={form.customer_phone} onChange={e=>setForm({...form,customer_phone:e.target.value})} className="px-4 py-3 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none sm:col-span-2" data-testid="checkout-phone" />
              <textarea placeholder="Catatan (opsional)" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} rows={2} className="px-4 py-3 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none sm:col-span-2" />
            </div>
          </div>

          <div className="lumina-card p-7">
            <h2 className="font-serif text-2xl mb-5">Metode Pembayaran</h2>
            <div className="space-y-3">
              {PAYMENTS.map(p => (
                <label key={p.id} className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition ${pm === p.id ? "border-[#6B4BA3] bg-[#E8F0FA]/50" : "border-[#E2DFED]"}`} data-testid={`payment-${p.id}`}>
                  <input type="radio" checked={pm === p.id} onChange={() => setPm(p.id)} className="mt-1 accent-[#6B4BA3]" />
                  <div className="flex-1">
                    <div className="font-medium flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#6B4BA3]" /> {p.label}</div>
                    <div className="text-xs text-[#5C5870] mt-1">{p.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <p className="text-xs text-[#5C5870] mt-3">*Mode demo: pembayaran akan dikonfirmasi otomatis. Integrasi gateway aktif setelah API key dipasang.</p>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="lumina-card p-7 sticky top-28">
            <h2 className="font-serif text-2xl mb-5">Total</h2>
            <div className="flex gap-2 mb-5">
              <input placeholder="Kode voucher" value={code} onChange={e=>setCode(e.target.value.toUpperCase())} className="flex-1 px-3 py-2.5 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none text-sm" data-testid="voucher-input" />
              <button onClick={applyVoucher} className="lumina-btn-secondary !px-4 !py-2.5 text-sm" data-testid="voucher-apply"><Tag className="w-4 h-4" /> Pakai</button>
            </div>
            <div className="space-y-2 text-sm pb-4 border-b border-[#E2DFED]">
              <div className="flex justify-between"><span className="text-[#5C5870]">Subtotal</span><span>{formatRupiah(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-[#6B4BA3]"><span>Diskon ({voucher?.code})</span><span>-{formatRupiah(discount)}</span></div>}
            </div>
            <div className="flex justify-between items-baseline py-4">
              <span className="font-medium">Total</span>
              <span className="font-serif text-3xl text-[#6B4BA3]">{formatRupiah(total)}</span>
            </div>
            <button onClick={placeOrder} disabled={loading} className="lumina-btn-primary w-full" data-testid="place-order-btn">{loading ? "Memproses…" : "Bayar Sekarang"}</button>
            <p className="text-xs text-[#5C5870] mt-3 text-center">Coba voucher: LUMINA10 atau NEWUSER50</p>
          </div>
        </div>
      </div>
    </div>
  );
}
